import React from "react";
import {
    Alert, AlertIcon, Badge, Box, Flex, Icon, SimpleGrid, Spinner, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdMemory, MdStorage } from "react-icons/md";
import Card from "horizon/components/card/Card";

/**
 * Renders the memory usage sub-section inside cache stats.
 * @param {object} props
 * @param {object} props.memory - Memory stats with used_memory_human and used_memory_peak_human
 * @returns {React.ReactElement|null}
 */
function MemoryUsageSection({ memory }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    if (!memory) return null;
    return (
        <Box mb="20px" p="14px" bg={bg} borderRadius="12px">
            <Flex align="center" gap="8px" mb="10px">
                <Icon as={MdMemory} w="20px" h="20px" color="blue.400" />
                <Text fontSize="sm" fontWeight="700">Memory usage</Text>
            </Flex>
            <SimpleGrid columns={2} spacing="12px">
                <Box>
                    <Text fontSize="xs" color={subColor}>Current</Text>
                    <Text fontSize="md" fontWeight="600" color={valColor}>{memory.used_memory_human}</Text>
                </Box>
                <Box>
                    <Text fontSize="xs" color={subColor}>Peak</Text>
                    <Text fontSize="md" fontWeight="600" color={valColor}>{memory.used_memory_peak_human}</Text>
                </Box>
            </SimpleGrid>
        </Box>
    );
}

/**
 * Renders a single cache key type row with prefix and count.
 * @param {object} props
 * @param {string} props.name - Cache key category name
 * @param {object} props.data - Object with prefix and count properties
 * @returns {React.ReactElement}
 */
function CacheKeyRow({ name, data }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    return (
        <Box p="14px" bg={bg} borderRadius="12px" borderLeft="4px solid" borderLeftColor="brand.500">
            <Flex justify="space-between" align="center">
                <Box>
                    <Text fontSize="sm" fontWeight="600" color={valColor}>{name}</Text>
                    <Text fontSize="xs" color={subColor}>Prefix: {data.prefix}</Text>
                </Box>
                <Badge colorScheme="brand" borderRadius="6px" px="8px" fontWeight="700">{data.count} keys</Badge>
            </Flex>
        </Box>
    );
}

/**
 * Renders the list of cache key categories from stats data.
 * @param {object} props
 * @param {object} props.cacheStats - Full cache stats object (memory key excluded from iteration)
 * @returns {React.ReactElement}
 */
function CacheKeysList({ cacheStats }) {
    return (
        <>
            <Text fontSize="sm" fontWeight="700" mb="12px">Cached keys by type</Text>
            <Stack spacing="10px">
                {Object.entries(cacheStats).map(([name, data]) => {
                    if (name === "memory") return null;
                    return <CacheKeyRow key={name} name={name} data={data} />;
                })}
            </Stack>
        </>
    );
}

/**
 * Renders the body of the cache stats card: loading, data, or empty state.
 * @param {Object} props
 * @param {boolean} props.loading - Whether stats are being fetched
 * @param {Object|null} props.cacheStats - Cache statistics data
 * @returns {React.ReactElement}
 */
function CacheStatsBody({ loading, cacheStats }) {
    if (loading) {
        return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    }
    if (cacheStats) {
        return (
            <Box>
                <MemoryUsageSection memory={cacheStats.memory} />
                <CacheKeysList cacheStats={cacheStats} />
            </Box>
        );
    }
    return (
        <Alert status="info" borderRadius="10px" fontSize="sm">
            <AlertIcon />No statistics available. Click &quot;Refresh stats&quot; to load data.
        </Alert>
    );
}

/**
 * Card displaying cache statistics: memory usage and keys by prefix.
 * Shows a loading spinner, stats content, or an informational alert.
 * @param {object} props
 * @param {boolean} props.loading - Whether stats are being fetched
 * @param {object|null} props.cacheStats - Cache statistics data from the API
 * @returns {React.ReactElement}
 */
function CacheStatsCard({ loading, cacheStats }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Card>
            <Flex align="center" gap="8px" mb="16px">
                <Icon as={MdStorage} w="22px" h="22px" color="brand.500" />
                <Text fontSize="lg" fontWeight="700" color={titleColor}>Cache statistics</Text>
            </Flex>
            <CacheStatsBody loading={loading} cacheStats={cacheStats} />
        </Card>
    );
}

export default CacheStatsCard;
